import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { ChangelogCard } from "@redotech/redo-merchant-inventory-app/intelligent-inventory/components/changelog-card";
import { Flex } from "@redotech/redo-web/flex";
import redoLogoAvatarUrl from "@redotech/redo-web/icon-old/logo-bg.png?url";
import { Text } from "@redotech/redo-web/text";
import type {
  ChatAddToolApproveResponseFunction,
  DynamicToolUIPart,
  FileUIPart,
} from "ai";
import classNames from "classnames";
import { memo, useContext, useMemo } from "react";
import {
  readAskRedoConfirmationData,
  readExecuteCodeWrites,
} from "../confirmation-data";
import { tryParseCustomAnalyticsReportAction } from "../custom-analytics-report-tool-actions";
import {
  AskRedoEscalateToHumanCard,
  AskRedoEscalateToHumanPendingCard,
} from "../escalation/ask-redo-escalate-to-human-card";
import { useEscalationAuthor } from "../escalation/escalation-author-context";
import { isHumanEscalationMessage } from "../escalation/intercom-to-uimessage";
import {
  CREATE_MANUAL_FULFILLMENT_GROUPS,
  manualOrderWritesToChangelogEntries,
} from "../manual-order-changelog";
import type { AskRedoUIMessage } from "../types";
import { AskRedoConfirmation } from "./ask-redo-confirmation";
import { AskRedoFilePart } from "./ask-redo-file-part";
import { AskRedoMarkdown } from "./ask-redo-markdown";
import { AskRedoMessageActions } from "./ask-redo-message-actions";
import * as styles from "./ask-redo-message.module.css";
import { AskRedoReasoning } from "./ask-redo-reasoning";
import { AskRedoToolGroup } from "./ask-redo-tool-group";
import { tryRenderInlineToolOutput } from "./tool-renderers";
import { ConversionLinkButton } from "./tool-renderers/conversion-link-button";
import {
  CustomAnalyticsReportActionButton,
  stripCustomReportMarkdownLink,
} from "./tool-renderers/custom-analytics-report-actions";
import { FlowEditPreviewCard } from "./tool-renderers/flow-edit-preview-card";
import { FlowStagedActions } from "./tool-renderers/flow-staged-actions";
import {
  stripConversionMarkdownLinks,
  tryParseConversionLinkAction,
} from "./tool-renderers/parse-conversion-links";
import {
  readFlowEditPreview,
  readReturnFlowStaged,
} from "./tool-renderers/parse-flow-edit-preview";
import {
  readReturnMutationConfirmation,
  returnMutationApproveLabel,
} from "./tool-renderers/parse-return-mutation-confirmation";
import { PresentImageOptionsCard } from "./tool-renderers/present-image-options-card";
import { ResendLabelCard } from "./tool-renderers/resend-label-card";
import { ReturnLabelCard } from "./tool-renderers/return-label-card";
import { ReturnResetCard } from "./tool-renderers/return-reset-card";

/**
 * Renders a single UIMessage. Tool parts are special-cased:
 *   - approval-requested / approval-responded → inline confirmation card
 *     (responded stays in this branch to avoid a jarring re-mount as a
 *     spinning row while the server acks the decision)
 *   - tool with a registered inline renderer (e.g. `get_return_data`) →
 *     promoted out of the group as its own styled card
 *   - everything else → collapsed into one `<AskRedoToolGroup>` so a
 *     multi-tool turn doesn't stack N cards
 *
 * Ask Redo uses no static `tools` map on `useChat`, so every tool
 * arrives as a `DynamicToolUIPart` with `toolName` as a runtime string.
 */
export interface AskRedoMessageProps {
  message: AskRedoUIMessage;
  isStreaming: boolean;
  /** When true, render the per-message actions row (thumbs / copy)
   *  under the assistant bubble. The conversation hides this on the
   *  in-progress streaming message so actions only appear once a
   *  reply is finalized. */
  showActions: boolean;
  addToolApprovalResponse: ChatAddToolApproveResponseFunction;
  /** Sends a plain-text user turn — used by interactive tool cards
   *  (e.g. the present-image-options carousel) to reply on click. */
  onSendMessage: (text: string) => void;
  /** Reconciles results persisted by server-side tool workflows. */
  onToolSettled?: (toolCallId: string, output: unknown) => void;
  /** Past ask-user-question cards are kept visible for context, but
   *  should not be answerable again after a later user turn exists. */
  disableQuestionResponses?: boolean;
  /** Backs the flow preview's Edit button (stage a return-flow draft). */
  threadId?: string;
}

export const AskRedoMessage = memo(function AskRedoMessage({
  message,
  isStreaming,
  showActions,
  addToolApprovalResponse,
  onSendMessage,
  onToolSettled,
  disableQuestionResponses = false,
  threadId,
}: AskRedoMessageProps) {
  const isUser = message.role === "user";
  const isHuman =
    !isUser &&
    message.role === "assistant" &&
    isHumanEscalationMessage(message);
  const author = useEscalationAuthor();
  const authorAvatarUrl = author.avatarUrl?.trim() || null;
  const rpcClient = useContext(RedoMerchantRpcClientContext);
  const stageReturnFlowDraft = useMemo(() => {
    if (!rpcClient || !threadId) {
      return undefined;
    }
    return async (draftId: string, toolCallId: string) => {
      try {
        const response = await rpcClient.stageAskRedoReturnFlowDraft({
          threadId,
          draftId,
          toolCallId,
        });
        if (response.status !== "saved") {
          return false;
        }
        onToolSettled?.(toolCallId, response.result);
        return true;
      } catch {
        return false;
      }
    };
  }, [onToolSettled, rpcClient, threadId]);
  const rendered = renderParts(
    message.parts,
    isStreaming,
    addToolApprovalResponse,
    onSendMessage,
    disableQuestionResponses,
    stageReturnFlowDraft,
  );
  const copyText = !isUser && !isHuman ? collectMessageText(message) : "";
  const renderActions =
    showActions && !isUser && !isHuman && copyText.length > 0;

  if (isHuman) {
    // Human (support agent) replies render with the agent's avatar
    // outside the bubble (left of it). The agent's name shows once
    // in the EscalationStatusBanner ("Connected with Casey"), so
    // we omit it above each bubble to avoid repetition.
    return (
      <Flex
        align="flex-start"
        className={styles.assistantRow}
        gap="sm"
        justify="flex-start"
        w="full"
      >
        <img
          alt={authorAvatarUrl ? (author.name ?? "Support") : ""}
          className={styles.humanAvatar}
          referrerPolicy="no-referrer"
          src={authorAvatarUrl ?? redoLogoAvatarUrl}
        />
        <Flex
          aria-label={`${author.name ?? "Support"} said`}
          className={classNames(styles.bubble, styles.assistantBubble)}
          dir="column"
          flex="1 1 auto"
          gap="md"
          maxW="full"
          minW="none"
        >
          {rendered}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      className={isUser ? undefined : styles.assistantRow}
      justify={isUser ? "flex-end" : "flex-start"}
      w="full"
    >
      <Flex
        aria-label={isUser ? "You said" : "Assistant said"}
        className={classNames(
          styles.bubble,
          isUser ? styles.userBubble : styles.assistantBubble,
        )}
        dir="column"
        flex={isUser ? undefined : "1 1 auto"}
        gap="md"
        maxW={isUser ? undefined : "full"}
        minW="none"
      >
        {rendered}
        {renderActions && (
          <AskRedoMessageActions className={styles.actions} text={copyText} />
        )}
      </Flex>
    </Flex>
  );
});

/**
 * Flatten an assistant message's text-bearing parts into a single
 * string for the copy button. Tool parts and files are skipped — the
 * goal is "what the assistant said," not a JSON dump.
 */

function collectMessageText(message: AskRedoUIMessage): string {
  const pieces: string[] = [];
  for (const part of message.parts ?? []) {
    if (part.type === "text" && part.text.length > 0) {
      pieces.push(part.text);
    }
  }
  return pieces.join("\n\n");
}

type AskRedoMessagePart = AskRedoUIMessage["parts"][number];

function renderParts(
  parts: readonly AskRedoMessagePart[],
  isStreaming: boolean,
  addToolApprovalResponse: ChatAddToolApproveResponseFunction,
  onSendMessage: (text: string) => void,
  disableQuestionResponses: boolean,
  stageReturnFlowDraft?: (
    draftId: string,
    toolCallId: string,
  ) => Promise<boolean>,
): React.ReactNode[] {
  const result: React.ReactNode[] = [];
  let toolGroup: DynamicToolUIPart[] = [];
  let previousRenderedPart: "tool-card" | "other" | null = null;
  const deferredToolCards: React.ReactNode[] = [];
  /** One button per destination — repeated reads shouldn't stack duplicates. */
  const deferredConversionLinks = new Set<string>();
  let confirmationCardOwnsText = false;
  const shouldStripCustomReportMarkdownLink = parts.some((part) => {
    if (!isDynamicTool(part) || part.state !== "output-available") {
      return false;
    }
    return (
      tryParseCustomAnalyticsReportAction(part.toolName, part.output)?.type ===
      "view-report"
    );
  });

  const flushGroup = () => {
    if (toolGroup.length === 0) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const groupKey = `tools-${result.length}-${toolGroup[0]!.toolCallId}`;
    result.push(<AskRedoToolGroup key={groupKey} tools={toolGroup} />);
    toolGroup = [];
    previousRenderedPart = "other";
  };

  for (const [idx, part] of parts.entries()) {
    if (isDynamicTool(part)) {
      confirmationCardOwnsText = false;
    }
    if (isDynamicTool(part) && part.toolName === "escalate-to-human") {
      // Special-case: escalate-to-human owns its own multi-state card.
      // The card creates the Intercom escalation on approve (the
      // generic confirmation path can't — it doesn't know how to
      // build chat history or wire EscalationContext).
      flushGroup();
      const approvalId = isAwaitingApproval(part) ? part.approval.id : "";
      if (!approvalId) {
        if (isEscalationPreparingState(part.state)) {
          result.push(
            <AskRedoEscalateToHumanPendingCard
              key={`escalate-pending-${idx}`}
            />,
          );
          previousRenderedPart = "other";
        }
        // Resolved / denied / errored states do not need another card
        // here — escalation banner / error handling owns those states.
        continue;
      }
      result.push(
        <AskRedoEscalateToHumanCard
          addToolApprovalResponse={addToolApprovalResponse}
          approvalId={approvalId}
          input={part.input}
          key={`escalate-${idx}`}
          resolved={part.state === "approval-responded"}
        />,
      );
      previousRenderedPart = "other";
      continue;
    }

    if (isDynamicTool(part) && isAwaitingApproval(part)) {
      flushGroup();
      const approvalId = part.approval.id;
      // Keyed on the payload shape, not the tool name, so the producing tool
      // can be renamed without dropping the preview.
      const flowEditPreview = readFlowEditPreview(part.input);
      if (flowEditPreview) {
        const draftId =
          typeof (part.input as { draftId?: unknown } | undefined)?.draftId ===
          "string"
            ? (part.input as { draftId: string }).draftId
            : undefined;
        result.push(
          <FlowEditPreviewCard
            key={`approval-${idx}`}
            onApprove={() =>
              addToolApprovalResponse({ id: approvalId, approved: true })
            }
            onDeny={() =>
              addToolApprovalResponse({ id: approvalId, approved: false })
            }
            onStageInEditor={
              stageReturnFlowDraft && draftId
                ? () => stageReturnFlowDraft(draftId, part.toolCallId)
                : undefined
            }
            preview={flowEditPreview}
          />,
        );
        previousRenderedPart = "other";
        continue;
      }
      const returnMutation = readReturnMutationConfirmation(part.input);
      if (returnMutation) {
        result.push(
          <AskRedoConfirmation
            approveLabel={returnMutationApproveLabel(returnMutation)}
            denyLabel="Cancel"
            description={returnMutation.warnings.join(" ")}
            key={`approval-${idx}`}
            onApprove={() =>
              addToolApprovalResponse({ id: approvalId, approved: true })
            }
            onDeny={() =>
              addToolApprovalResponse({ id: approvalId, approved: false })
            }
            title={returnMutation.summary}
          />,
        );
        previousRenderedPart = "other";
        continue;
      }
      const executeCodeWrites =
        part.toolName === "execute_code"
          ? readExecuteCodeWrites(part.input)
          : undefined;
      if (executeCodeWrites) {
        const onApprove = () =>
          addToolApprovalResponse({ id: approvalId, approved: true });
        const onReject = () =>
          addToolApprovalResponse({ id: approvalId, approved: false });
        const { inventoryChangelog, rpcWrites, summary, confirmationPrompt } =
          executeCodeWrites;
        // Inventory has a special case rich changelog card that predates this
        // system being generic; manual-order writes are adapted into it too.
        const manualOrderWrites = rpcWrites.filter(
          (write) => write.name === CREATE_MANUAL_FULFILLMENT_GROUPS,
        );
        const otherRpcWrites = rpcWrites.filter(
          (write) => write.name !== CREATE_MANUAL_FULFILLMENT_GROUPS,
        );
        const manualOrderEntries =
          manualOrderWritesToChangelogEntries(manualOrderWrites);
        const changelog =
          manualOrderEntries &&
          (inventoryChangelog || manualOrderEntries.length > 0)
            ? [...(inventoryChangelog ?? []), ...manualOrderEntries]
            : undefined;
        if (otherRpcWrites.length === 0 && changelog) {
          result.push(
            <ChangelogCard
              confirmation={{
                toolCallId: approvalId,
                toolName: part.toolName,
                confirmationData: { changelog, summary },
                confirmationPrompt: confirmationPrompt ?? "Proceed?",
              }}
              key={`approval-${idx}`}
              onConfirm={onApprove}
              onReject={onReject}
            />,
          );
        } else {
          result.push(
            <AskRedoConfirmation
              approveLabel="Apply changes"
              denyLabel="Cancel"
              description={summary ?? confirmationPrompt}
              key={`approval-${idx}`}
              onApprove={onApprove}
              onDeny={onReject}
              title="Apply these changes?"
            />,
          );
        }
        previousRenderedPart = "other";
        continue;
      }
      const copy = confirmationCopyFor(part.toolName, part.input);
      result.push(
        <AskRedoConfirmation
          approveLabel={copy.approveLabel}
          denyLabel={copy.denyLabel}
          description={copy.description}
          key={`approval-${idx}`}
          onApprove={() =>
            addToolApprovalResponse({ id: approvalId, approved: true })
          }
          onDeny={() =>
            addToolApprovalResponse({ id: approvalId, approved: false })
          }
          title={copy.title}
        />,
      );
      previousRenderedPart = "other";
      continue;
    }

    if (isDynamicTool(part) && part.toolName === "present-image-options") {
      // Interactive carousel: data lives in the tool *input* (the
      // output is empty), and clicking sends a user turn. The generic
      // output-shape renderer can't express either, so it's special-
      // cased here like escalate-to-human. Render once the input is
      // available (skip the bare input-streaming state).
      if (part.state === "input-streaming") {
        continue;
      }
      flushGroup();
      result.push(
        <PresentImageOptionsCard
          disabled={isStreaming}
          input={part.input}
          key={`carousel-${idx}`}
          onSelect={onSendMessage}
          toolCallId={part.toolCallId}
        />,
      );
      previousRenderedPart = "tool-card";
      continue;
    }

    if (
      isDynamicTool(part) &&
      part.toolName === "resend_return_label" &&
      part.state === "output-available"
    ) {
      flushGroup();
      result.push(
        <ResendLabelCard
          disabled={isStreaming}
          input={part.input}
          key={`resend-label-${idx}`}
          toolCallId={part.toolCallId}
        />,
      );
      previousRenderedPart = "tool-card";
      continue;
    }

    if (
      isDynamicTool(part) &&
      part.toolName === "manage_return_label" &&
      part.state === "output-available"
    ) {
      flushGroup();
      result.push(
        <ReturnLabelCard
          disabled={isStreaming}
          input={part.input}
          key={`return-label-${idx}`}
          toolCallId={part.toolCallId}
        />,
      );
      previousRenderedPart = "tool-card";
      continue;
    }

    if (
      isDynamicTool(part) &&
      part.toolName === "reset_return" &&
      part.state === "output-available" &&
      isPresentedResetReturnOutput(part.output)
    ) {
      flushGroup();
      result.push(
        <ReturnResetCard
          disabled={isStreaming}
          input={part.input}
          key={`return-reset-${idx}`}
          output={part.output}
          toolCallId={part.toolCallId}
        />,
      );
      previousRenderedPart = "tool-card";
      continue;
    }

    if (isDynamicTool(part)) {
      const inline = tryRenderInlineToolOutput(
        part.toolName,
        part.state,
        part.state === "output-available" ? part.output : undefined,
        { disabled: isStreaming || disableQuestionResponses, onSendMessage },
      );
      const customAnalyticsAction =
        part.state === "output-available"
          ? tryParseCustomAnalyticsReportAction(part.toolName, part.output)
          : null;
      if (customAnalyticsAction) {
        const codeOutput =
          part.toolName === "execute_code" &&
          part.state === "output-available" &&
          isPlainObject(part.output)
            ? part.output
            : null;
        const codeError =
          typeof codeOutput?.error === "string" ? codeOutput.error : null;
        const hasCodeText =
          typeof codeOutput?.stdout === "string" &&
          codeOutput.stdout.trim().length > 0;
        confirmationCardOwnsText =
          customAnalyticsAction.type === "confirm" &&
          customAnalyticsAction.plan !== null &&
          inline === null &&
          !codeError &&
          !hasCodeText;
        flushGroup();
        deferredToolCards.push(
          <CustomAnalyticsReportActionButton
            action={customAnalyticsAction}
            disabled={isStreaming}
            key={`custom-analytics-${idx}`}
            onSendMessage={onSendMessage}
            toolCallId={part.toolCallId}
          />,
        );
        if (codeError) {
          result.push(
            <Text
              fontSize="sm"
              key={`custom-analytics-error-${idx}`}
              textColor="secondary"
            >
              {codeError}
            </Text>,
          );
          previousRenderedPart = "other";
        }
        if (inline === null) {
          continue;
        }
      }

      const flowStaged =
        part.state === "output-available"
          ? readReturnFlowStaged(part.output)
          : undefined;
      if (flowStaged) {
        flushGroup();
        deferredToolCards.push(
          <FlowStagedActions
            disabled={isStreaming}
            flowType={flowStaged.flowType}
            key={`flow-staged-${idx}`}
          />,
        );
        continue;
      }

      const conversionLinkAction =
        part.state === "output-available"
          ? tryParseConversionLinkAction(part.toolName, part.output)
          : null;
      if (conversionLinkAction) {
        flushGroup();
        const alreadyLinked = deferredConversionLinks.has(
          conversionLinkAction.url,
        );
        if (!alreadyLinked) {
          deferredConversionLinks.add(conversionLinkAction.url);
          deferredToolCards.push(
            <ConversionLinkButton
              action={conversionLinkAction}
              key={`conversion-link-${idx}`}
            />,
          );
        }
        continue;
      }

      if (inline !== null) {
        flushGroup();
        result.push(
          <Flex
            className={styles.inlineToolCard}
            key={`tool-card-${idx}`}
            minW="none"
            w="full"
          >
            {inline}
          </Flex>,
        );
        previousRenderedPart = "tool-card";
        continue;
      }
      toolGroup.push(part);
      continue;
    }

    flushGroup();

    if (part.type === "text") {
      if (confirmationCardOwnsText) {
        continue;
      }
      let content = part.text;
      if (shouldStripCustomReportMarkdownLink) {
        content = stripCustomReportMarkdownLink(content);
      }
      content = stripConversionMarkdownLinks(content);
      if (!content.trim()) {
        continue;
      }
      const markdown = (
        <AskRedoMarkdown content={content} key={`text-${idx}`} />
      );
      result.push(
        previousRenderedPart === "tool-card" ? (
          <Flex key={`text-${idx}`} mt="xl">
            {markdown}
          </Flex>
        ) : (
          markdown
        ),
      );
      previousRenderedPart = "other";
      continue;
    }

    if (part.type === "reasoning") {
      result.push(
        <AskRedoReasoning
          isStreaming={part.state === "streaming" && isStreaming}
          key={`reasoning-${idx}`}
          text={part.text}
        />,
      );
      previousRenderedPart = "other";
      continue;
    }

    if (part.type === "file") {
      result.push(renderFilePart(part, idx));
      previousRenderedPart = "other";
      continue;
    }

    // Source / data / step-start parts — not rendered today.
  }

  flushGroup();
  result.push(...deferredToolCards);
  return result;
}

function renderFilePart(part: FileUIPart, idx: number): React.ReactNode {
  return <AskRedoFilePart key={`file-${idx}`} part={part} />;
}

function isDynamicTool(part: AskRedoMessagePart): part is DynamicToolUIPart {
  return part.type === "dynamic-tool";
}

function isPresentedResetReturnOutput(output: unknown): boolean {
  if (typeof output !== "object" || output === null) {
    return false;
  }
  return Reflect.get(output, "presented") === true;
}

/** Type guard so callers can read `part.approval` without a `!`. */
function isAwaitingApproval(
  part: DynamicToolUIPart,
): part is DynamicToolUIPart & {
  state: "approval-requested" | "approval-responded";
  approval: { id: string };
} {
  return (
    part.state === "approval-requested" || part.state === "approval-responded"
  );
}

function isEscalationPreparingState(
  state: DynamicToolUIPart["state"],
): boolean {
  return state === "input-streaming" || state === "input-available";
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Merchant-facing copy for the approval card.
 *
 * Default is minimal and generic; tools that deserve special copy
 * (like escalation) add a branch. Keep this user-facing — no raw
 * JSON, no "approve/deny" jargon unless the tool genuinely feels
 * like an approval (e.g. a destructive admin action).
 */
interface ConfirmationCopy {
  title: string;
  description?: string;
  approveLabel: string;
  denyLabel: string;
}

function confirmationCopyFor(
  toolName: string,
  input: unknown,
): ConfirmationCopy {
  const prompt = readAskRedoConfirmationData(input)?.confirmationPrompt;
  // `escalate-to-human` is special-cased in renderParts (see
  // AskRedoEscalateToHumanCard) — anything reaching this fallback is a
  // generic destructive/admin tool with no bespoke copy registered.
  const prettyName = toolName.replace(/[-_]/g, " ");
  return {
    title: `Confirm: ${prettyName}`,
    description: prompt,
    approveLabel: "Confirm",
    denyLabel: "Cancel",
  };
}
